import { MAZE_CELL_SIZE, MAZE_WALL_CUBE_INSET_PX, MAZE_WALL_THICKNESS } from '@/constants/maze';
import {
  appendCometSample,
  COMET_MIN_SAMPLE_DIST_PX,
  drawCometTrailGraphics,
  pruneExpiredCometSamples,
  type CometSample,
} from '@/lib/maze/maze-canvas-comet';
import { drawMazeWallProximityNeon } from '@/lib/maze/maze-wall-proximity-neon';
import { useDemoStore } from '@/store/demo';
import type { MazeStagePixiPalette } from '@/theme/maze-stage-palette';
import { Direction } from '@/types/maze';
import { isDemoTutorialInputBlocked } from '@/utils/maze/demo-tutorial';
import * as PIXI from 'pixi.js';
import type { RefObject } from 'react';
import { useEffect } from 'react';
import {
  MAX_MAZE_DIM,
  type MazeMoveEventDetail,
  type ViewportTransformState,
} from './maze-canvas-types';

type GestureMode = 'idle' | 'single' | 'multi';

type TouchPoint = {
  readonly startX: number;
  readonly startY: number;
  currentX: number;
  currentY: number;
};

type SwipeCandidate = {
  readonly pointerId: number;
  readonly startX: number;
  readonly startY: number;
  readonly startedAt: number;
};

type MultiTouchStart = {
  readonly startScale: number;
  readonly startX: number;
  readonly startY: number;
  readonly worldXAtCentroid: number;
  readonly worldYAtCentroid: number;
  readonly startDistance: number;
};

type GestureState = {
  mode: GestureMode;
  pointers: Map<number, TouchPoint>;
  swipeCandidate: SwipeCandidate | null;
  multiTouchStart: MultiTouchStart | null;
};

const DEFAULT_VIEWPORT_SCALE = 1;
const MIN_VIEWPORT_SCALE = DEFAULT_VIEWPORT_SCALE;
const MAX_VIEWPORT_SCALE = 3;
const SWIPE_MIN_DISTANCE_PX = MAZE_CELL_SIZE * 0.45;
const SWIPE_MAX_DURATION_MS = 500;
const MAX_GLOW_RADIUS_CELL = 3;

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function getDirectionFromDelta(dx: number, dy: number): Direction | null {
  const absX = Math.abs(dx);
  const absY = Math.abs(dy);
  if (Math.max(absX, absY) < SWIPE_MIN_DISTANCE_PX) {
    return null;
  }

  if (absX > absY) {
    return dx > 0 ? Direction.RIGHT : Direction.LEFT;
  }

  return dy > 0 ? Direction.DOWN : Direction.UP;
}

function getTwoPointers(pointers: Map<number, TouchPoint>): [TouchPoint, TouchPoint] | null {
  const iterator = pointers.values();
  const first = iterator.next();
  const second = iterator.next();
  if (first.done || second.done) {
    return null;
  }

  return [first.value, second.value];
}

function getCentroid(first: TouchPoint, second: TouchPoint): { x: number; y: number } {
  return {
    x: (first.currentX + second.currentX) / 2,
    y: (first.currentY + second.currentY) / 2,
  };
}

function getDistance(first: TouchPoint, second: TouchPoint): number {
  return Math.hypot(first.currentX - second.currentX, first.currentY - second.currentY);
}

export function useMazePixiApp(
  containerRef: RefObject<HTMLDivElement | null>,
  appRef: RefObject<PIXI.Application | null>,
  viewportRef: RefObject<PIXI.Container | null>,
  applyCanvasLayoutRef: RefObject<(() => void) | null>,
  resetViewportTransformRef: RefObject<(() => void) | null>,
  handleMoveRef: RefObject<(direction: string | Direction) => void>,
  trailRef: RefObject<PIXI.Graphics | null>,
  playerRef: RefObject<PIXI.Container | null>,
  cometSamplesRef: RefObject<CometSample[]>,
  mazePaletteRef: RefObject<MazeStagePixiPalette>,
  wallGlowInnerRef: RefObject<PIXI.Graphics | null>,
  wallGlowRef: RefObject<PIXI.Graphics | null>,
  mazeGridRef: RefObject<number[][]>,
  mazeColsRef: RefObject<number>,
  mazeRowsRef: RefObject<number>,
): void {
  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const app = new PIXI.Application({
      width: MAX_MAZE_DIM * MAZE_CELL_SIZE,
      height: MAX_MAZE_DIM * MAZE_CELL_SIZE,
      backgroundColor: mazePaletteRef.current.floor,
      antialias: true,
      resolution: 1,
      autoDensity: false,
    });

    const canvas = app.view as HTMLCanvasElement;
    canvas.style.touchAction = 'none';
    canvas.style.userSelect = 'none';
    canvas.style.setProperty('-webkit-user-select', 'none');
    canvas.style.setProperty('-ms-user-select', 'none');
    canvas.style.display = 'block';
    container.appendChild(canvas);
    appRef.current = app;

    const viewportTransform: ViewportTransformState = {
      scale: DEFAULT_VIEWPORT_SCALE,
      x: 0,
      y: 0,
    };
    const gestureState: GestureState = {
      mode: 'idle',
      pointers: new Map(),
      swipeCandidate: null,
      multiTouchStart: null,
    };
    const mousePan = { active: false, lastX: 0, lastY: 0 };

    const applyViewportTransform = (nextScale: number, nextX: number, nextY: number) => {
      const viewport = viewportRef.current;
      const appInstance = appRef.current;
      if (!viewport || !appInstance) {
        return;
      }

      const scale = clamp(nextScale, MIN_VIEWPORT_SCALE, MAX_VIEWPORT_SCALE);
      const screenWidth = appInstance.screen.width;
      const screenHeight = appInstance.screen.height;

      let x = nextX;
      let y = nextY;
      if (scale <= 1) {
        x = (screenWidth - screenWidth * scale) / 2;
        y = (screenHeight - screenHeight * scale) / 2;
      } else {
        x = clamp(x, screenWidth - screenWidth * scale, 0);
        y = clamp(y, screenHeight - screenHeight * scale, 0);
      }

      viewport.scale.set(scale);
      viewport.position.set(x, y);

      viewportTransform.scale = scale;
      viewportTransform.x = x;
      viewportTransform.y = y;
    };

    const resetViewportTransform = () => {
      gestureState.mode = 'idle';
      gestureState.pointers.clear();
      gestureState.swipeCandidate = null;
      gestureState.multiTouchStart = null;
      applyViewportTransform(DEFAULT_VIEWPORT_SCALE, 0, 0);
    };

    resetViewportTransformRef.current = resetViewportTransform;

    const applyCanvasLayout = () => {
      if (!containerRef.current || !appRef.current) {
        return;
      }
      const parent = containerRef.current.parentElement;
      if (!parent) {
        return;
      }
      const currentCanvas = appRef.current.view as HTMLCanvasElement;
      const sw = appRef.current.screen.width;
      const sh = appRef.current.screen.height;
      if (sw <= 0 || sh <= 0) {
        return;
      }
      const parentWidth = parent.clientWidth;
      const parentHeight = parent.clientHeight;
      if (parentWidth <= 0 || parentHeight <= 0) {
        return;
      }
      /** Small visual safe margin (2px per side) so outermost walls don't clip under panel border-radius. */
      const margin = 4;
      const safeWidth = Math.max(0, parentWidth - margin);
      const safeHeight = Math.max(0, parentHeight - margin);

      /** Fill play area width and height safely; fit the aspect ratio within both bounds. */
      const scale = Math.min(safeWidth / sw, safeHeight / sh);
      currentCanvas.style.width = `${sw * scale}px`;
      currentCanvas.style.height = `${sh * scale}px`;
      currentCanvas.style.display = 'block';
    };

    const triggerLayoutUpdate = () => {
      applyCanvasLayout();

      requestAnimationFrame(() => {
        applyCanvasLayout();
      });

      const delays = [50, 150, 300, 600];
      delays.forEach((delay) => {
        setTimeout(applyCanvasLayout, delay);
      });
    };

    applyCanvasLayoutRef.current = triggerLayoutUpdate;

    const shouldBlockTutorialInput = () =>
      isDemoTutorialInputBlocked(useDemoStore.getState().demoTutorialStep);

    const handleCustomMove = (event: Event) => {
      if (shouldBlockTutorialInput()) {
        return;
      }
      const mazeEvent = event as CustomEvent<MazeMoveEventDetail>;
      handleMoveRef.current(mazeEvent.detail.direction);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (shouldBlockTutorialInput()) {
        return;
      }
      switch (event.key) {
        case 'ArrowUp':
        case 'w':
        case 'W':
          handleMoveRef.current(Direction.UP);
          break;
        case 'ArrowDown':
        case 's':
        case 'S':
          handleMoveRef.current(Direction.DOWN);
          break;
        case 'ArrowLeft':
        case 'a':
        case 'A':
          handleMoveRef.current(Direction.LEFT);
          break;
        case 'ArrowRight':
        case 'd':
        case 'D':
          handleMoveRef.current(Direction.RIGHT);
          break;
        default:
          break;
      }
    };

    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();
      if (shouldBlockTutorialInput()) return;

      const rect = canvas.getBoundingClientRect();
      const scaleX = rect.width > 0 ? app.screen.width / rect.width : 1;
      const scaleY = rect.height > 0 ? app.screen.height / rect.height : 1;

      // Mouse position in PixiJS canvas pixel space
      const mx = (event.clientX - rect.left) * scaleX;
      const my = (event.clientY - rect.top) * scaleY;

      const zoomFactor = event.deltaY < 0 ? 1.12 : 1 / 1.12;
      const nextScale = clamp(viewportTransform.scale * zoomFactor, MIN_VIEWPORT_SCALE, MAX_VIEWPORT_SCALE);

      // Keep the world point under the cursor fixed during zoom
      const worldX = (mx - viewportTransform.x) / viewportTransform.scale;
      const worldY = (my - viewportTransform.y) / viewportTransform.scale;
      applyViewportTransform(nextScale, mx - worldX * nextScale, my - worldY * nextScale);
    };

    const getCanvasPoint = (event: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      const scaleX = rect.width > 0 ? app.screen.width / rect.width : 1;
      const scaleY = rect.height > 0 ? app.screen.height / rect.height : 1;
      return {
        x: (event.clientX - rect.left) * scaleX,
        y: (event.clientY - rect.top) * scaleY,
      };
    };

    const syncMultiTouchTransform = () => {
      const points = getTwoPointers(gestureState.pointers);
      if (!points || !gestureState.multiTouchStart) {
        return;
      }

      const [first, second] = points;
      const centroid = getCentroid(first, second);
      const distance = Math.max(getDistance(first, second), 1);
      const scaleRatio = distance / gestureState.multiTouchStart.startDistance;
      const nextScale = gestureState.multiTouchStart.startScale * scaleRatio;
      const nextX = centroid.x - gestureState.multiTouchStart.worldXAtCentroid * nextScale;
      const nextY = centroid.y - gestureState.multiTouchStart.worldYAtCentroid * nextScale;
      applyViewportTransform(nextScale, nextX, nextY);
    };

    const beginMultiTouch = () => {
      const points = getTwoPointers(gestureState.pointers);
      if (!points) {
        return;
      }

      const [first, second] = points;
      const centroid = getCentroid(first, second);
      const startDistance = Math.max(getDistance(first, second), 1);
      gestureState.mode = 'multi';
      gestureState.swipeCandidate = null;
      gestureState.multiTouchStart = {
        startScale: viewportTransform.scale,
        startX: viewportTransform.x,
        startY: viewportTransform.y,
        worldXAtCentroid: (centroid.x - viewportTransform.x) / viewportTransform.scale,
        worldYAtCentroid: (centroid.y - viewportTransform.y) / viewportTransform.scale,
        startDistance,
      };
    };

    const handlePointerDown = (event: PointerEvent) => {
      if (event.pointerType !== 'touch') {
        mousePan.active = true;
        mousePan.lastX = event.clientX;
        mousePan.lastY = event.clientY;
        canvas.setPointerCapture?.(event.pointerId);
        return;
      }

      const point = getCanvasPoint(event);
      gestureState.pointers.set(event.pointerId, {
        startX: point.x,
        startY: point.y,
        currentX: point.x,
        currentY: point.y,
      });

      if (canvas.setPointerCapture) {
        canvas.setPointerCapture(event.pointerId);
      }

      if (gestureState.pointers.size === 1) {
        gestureState.mode = 'single';
        gestureState.swipeCandidate = {
          pointerId: event.pointerId,
          startX: point.x,
          startY: point.y,
          startedAt: performance.now(),
        };
        gestureState.multiTouchStart = null;
        return;
      }

      gestureState.mode = 'multi';
      gestureState.swipeCandidate = null;
      beginMultiTouch();
      event.preventDefault();
    };

    const handlePointerMove = (event: PointerEvent) => {
      if (event.pointerType !== 'touch') {
        if (!mousePan.active) return;
        const rect = canvas.getBoundingClientRect();
        const cssScaleX = rect.width > 0 ? app.screen.width / rect.width : 1;
        const cssScaleY = rect.height > 0 ? app.screen.height / rect.height : 1;
        const dx = (event.clientX - mousePan.lastX) * cssScaleX;
        const dy = (event.clientY - mousePan.lastY) * cssScaleY;
        mousePan.lastX = event.clientX;
        mousePan.lastY = event.clientY;
        applyViewportTransform(viewportTransform.scale, viewportTransform.x + dx, viewportTransform.y + dy);
        event.preventDefault();
        return;
      }

      const point = gestureState.pointers.get(event.pointerId);
      if (!point) {
        return;
      }

      const nextPoint = getCanvasPoint(event);
      point.currentX = nextPoint.x;
      point.currentY = nextPoint.y;

      if (gestureState.mode === 'multi') {
        if (!gestureState.multiTouchStart) {
          beginMultiTouch();
        }
        syncMultiTouchTransform();
        event.preventDefault();
        return;
      }

      if (
        gestureState.mode === 'single' &&
        gestureState.swipeCandidate?.pointerId === event.pointerId
      ) {
        event.preventDefault();
      }
    };

    const handlePointerEnd = (event: PointerEvent) => {
      if (event.pointerType !== 'touch') {
        mousePan.active = false;
        if (canvas.hasPointerCapture?.(event.pointerId)) {
          canvas.releasePointerCapture(event.pointerId);
        }
        return;
      }

      const point = gestureState.pointers.get(event.pointerId);
      const endPoint = getCanvasPoint(event);
      if (point) {
        point.currentX = endPoint.x;
        point.currentY = endPoint.y;
      }
      const swipeCandidate = gestureState.swipeCandidate;
      const gestureWasSingle = gestureState.mode === 'single';

      gestureState.pointers.delete(event.pointerId);

      if (gestureWasSingle && swipeCandidate?.pointerId === event.pointerId && point) {
        const duration = performance.now() - swipeCandidate.startedAt;
        const direction =
          duration <= SWIPE_MAX_DURATION_MS
            ? getDirectionFromDelta(
                point.currentX - swipeCandidate.startX,
                point.currentY - swipeCandidate.startY,
              )
            : null;

        if (direction && !shouldBlockTutorialInput()) {
          handleMoveRef.current(direction);
        }
      }

      if (gestureState.pointers.size === 0) {
        gestureState.mode = 'idle';
        gestureState.swipeCandidate = null;
        gestureState.multiTouchStart = null;
      } else if (gestureState.mode === 'multi') {
        gestureState.swipeCandidate = null;
        if (gestureState.pointers.size >= 2) {
          beginMultiTouch();
        }
      }

      if (canvas.hasPointerCapture?.(event.pointerId)) {
        canvas.releasePointerCapture(event.pointerId);
      }

      if (gestureState.pointers.size > 0 && gestureState.mode === 'single') {
        gestureState.mode = 'multi';
        gestureState.swipeCandidate = null;
        gestureState.multiTouchStart = null;
        beginMultiTouch();
      }
    };

    const handlePointerCancel = (event: PointerEvent) => {
      handlePointerEnd(event);
    };

    window.addEventListener('resize', triggerLayoutUpdate);
    window.addEventListener('orientationchange', triggerLayoutUpdate);
    window.addEventListener('maze-move', handleCustomMove);
    window.addEventListener('keydown', handleKeyDown);

    const parentEl = container.parentElement;
    const resizeObserver = parentEl
      ? new ResizeObserver(() => {
          triggerLayoutUpdate();
        })
      : null;
    if (parentEl) {
      resizeObserver?.observe(parentEl);
    }

    canvas.addEventListener('pointerdown', handlePointerDown, { passive: false });
    canvas.addEventListener('pointermove', handlePointerMove, { passive: false });
    canvas.addEventListener('pointerup', handlePointerEnd, { passive: false });
    canvas.addEventListener('pointercancel', handlePointerCancel, { passive: false });
    canvas.addEventListener('wheel', handleWheel, { passive: false });

    // Reduce glow radius on touch devices to cut down scanned wall cells per frame.
    const isMobileTouch = typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0;
    const neonRadiusCells = isMobileTouch ? 2 : MAX_GLOW_RADIUS_CELL;

    // Track last position where neon was drawn; skip redraw when player hasn't moved.
    let lastNeonX = -Infinity;
    let lastNeonY = -Infinity;

    app.ticker.add(() => {
      const trail = trailRef.current;
      if (!trail) {
        return;
      }

      trail.clear();

      const player = playerRef.current;
      const samples = cometSamplesRef.current;
      const now = performance.now();
      const minDistSq = COMET_MIN_SAMPLE_DIST_PX * COMET_MIN_SAMPLE_DIST_PX;

      if (player) {
        appendCometSample(samples, player, now, minDistSq);
      }

      pruneExpiredCometSamples(samples, now);
      drawCometTrailGraphics(trail, samples, now, mazePaletteRef.current);

      // Render per-cube neon — only when the player has moved since the last draw.
      const wallGlowInner = wallGlowInnerRef.current;
      const wallGlow = wallGlowRef.current;
      const grid = mazeGridRef.current;
      if (wallGlowInner && wallGlow && player && grid) {
        const playerX = player.x;
        const playerY = player.y;
        const dx = playerX - lastNeonX;
        const dy = playerY - lastNeonY;

        if (dx * dx + dy * dy > 0.09) {
          wallGlowInner.clear();
          wallGlow.clear();

          const cs = MAZE_CELL_SIZE;
          const maxRadius = neonRadiusCells * cs;
          const wallPx = Math.max(cs - 2 * MAZE_WALL_CUBE_INSET_PX, MAZE_WALL_THICKNESS);

          drawMazeWallProximityNeon({
            wallGlowInner,
            wallGlow,
            grid,
            playerX,
            playerY,
            cols: mazeColsRef.current ?? 0,
            rows: mazeRowsRef.current ?? 0,
            maxRadius,
            cellSize: cs,
            wallPx,
            cornerRadius: 5,
            palette: mazePaletteRef.current,
          });

          lastNeonX = playerX;
          lastNeonY = playerY;
        }
      }
    });

    triggerLayoutUpdate();

    return () => {
      applyCanvasLayoutRef.current = null;
      resetViewportTransformRef.current = null;
      window.removeEventListener('resize', triggerLayoutUpdate);
      window.removeEventListener('orientationchange', triggerLayoutUpdate);
      window.removeEventListener('maze-move', handleCustomMove);
      window.removeEventListener('keydown', handleKeyDown);
      canvas.removeEventListener('pointerdown', handlePointerDown);
      canvas.removeEventListener('pointermove', handlePointerMove);
      canvas.removeEventListener('pointerup', handlePointerEnd);
      canvas.removeEventListener('pointercancel', handlePointerCancel);
      canvas.removeEventListener('wheel', handleWheel);
      resizeObserver?.disconnect();
      app.destroy(true, { children: true, texture: true, baseTexture: true });
      appRef.current = null;
      viewportRef.current = null;
    };
    // Refs are stable; mount PIXI once like the original component.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
